import React, { useState, useEffect } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Avatar, Typography, Button, IconButton,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    TextField, InputAdornment, Tooltip, Chip, Stack,
    CircularProgress, MenuItem, Select, FormControl, InputLabel,
    Pagination, Skeleton, Autocomplete, Drawer, 
} from '@mui/material';
import {  FaSearch as SearchIcon } from 'react-icons/fa';
import { MdAddShoppingCart } from "react-icons/md";
import {  MdDeleteForever } from 'react-icons/md';
import { FaEdit } from "react-icons/fa";
import { FiFilter as FilterIcon} from 'react-icons/fi';
import ProductForm from './ProductForm';
import { getData, postFileUpload, deleteData, putData, postData } from '../../Webservice/webservice';
import { FaRegCheckCircle } from "react-icons/fa";
import { MdPresentToAll } from "react-icons/md";

const statusOptions = [
    { value: 'approved', label: 'approved', color: 'success' },
    { value: 'pending', label: 'pending', color: 'error' },
    { value: 'rejected', label: 'rejected', color: 'warning' },
];

const initialFormState = {
    title: "",
    description: "",
    key_features: "",
    how_to_use: "",
    category_id: "",
    subcategory_id: "",
    upc: "",
    product_images: [],
    unitType: "",
    unitValue: 0,
    unitName: "",
    status: "active",
    isPublished: false,
    brand: "",
    ideal_for: "",
    applied_for: "",
    container_type: "",
    country_of_orgin: "",
    color: "",
    createdBy: "",
    createdAt: "",
    gst: "",
    hsn_code: "",
    codAvailable: false,
    tags: [],
    b2c_mrp: '',
    b2c_discountType: '',
    b2c_discountValue: '',
    b2c_sellingPrice: '',
    minimumStockQuantity: '',
    // Dimensions (UI uses flat fields; backend expects `dimensions` object)
    length: '',
    width: '',
    height: '',
    dimensionUnit: '',
    // Optional weight support
    weight: '',
    weightUnit: ''
};

const PAGE_SIZE = 20;

const ProductManagement = () => {
    const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
    // State for UPC modal
    const [upcModalOpen, setUpcModalOpen] = useState(false);
    const [upcInput, setUpcInput] = useState('');
    const [upcVerifyResult, setUpcVerifyResult] = useState(null);
    const [upcResultModalOpen, setUpcResultModalOpen] = useState(false);
    const [upcVerifying, setUpcVerifying] = useState(false);
    // State for Request Form modal
    const [requestFormModalOpen, setRequestFormModalOpen] = useState(false);
    const [requestForm, setRequestForm] = useState({
        upc: '',
        maximumRetailPrice: '',
        b2c_sellingPrice: '',
        b2b_sellingPrice: ''
    });
    const [requestFormSubmitting, setRequestFormSubmitting] = useState(false);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [upcSearch, setUpcSearch] = useState('');
    const [hsnCode, setHsnCode] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [isPublishedFilter, setIsPublishedFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [showAddForm, setShowAddForm] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [form, setForm] = useState(initialFormState);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formMode, setFormMode] = useState('edit');
    const [stockDialogOpen, setStockDialogOpen] = useState(false);
    const [selectedProductStock, setSelectedProductStock] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [warehouseList, setWarehouseList] = useState([]);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [ownershipFilter, setOwnershipFilter] = useState('all');
    const [tempFilters, setTempFilters] = useState({
        searchTerm: searchTerm,
        upcSearch: upcSearch,
        hsnCode: hsnCode,
        statusFilter: statusFilter,
        isPublishedFilter: isPublishedFilter,
        categoryFilter: categoryFilter,
        ownershipFilter: ownershipFilter,
        subcategoryFilter: 'all',
    });
    const [subcategoryFilter, setSubcategoryFilter] = useState('all');
    const [filterSubcategories, setFilterSubcategories] = useState([]);
    
    // Check if user is vendor
    const isVendor = !!localStorage.getItem('vendorRole');
    const [allProducts, setAllProducts] = useState([]);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const params = { page: meta.page, limit: 20 };

            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            if (searchTerm) {
                params.title = searchTerm;
            }
            if (upcSearch) {
                params.upc = upcSearch;
            }
            if (hsnCode) {
                params.hsnCode = hsnCode;
            }
            if (categoryFilter !== 'all') {
                params.categoryId = categoryFilter;
            }
            if (subcategoryFilter !== 'all') {
                params.subcategoryId = subcategoryFilter;
            }
            if (isPublishedFilter !== 'all') {
                params.isPublished = isPublishedFilter === 'published' ? true : false;
            }

            const response = await getData("product/list", params, token, false);

            // ✅ Always use products array directly
            const productsArray = Array.isArray(response?.products)
                ? response.products
                : [];

            // ✅ NO FILTER – show everything
            setAllProducts(productsArray);
            setFilteredProducts(productsArray);

            setMeta({
                page: response?.meta?.page || 1,
                totalPages: response?.meta?.totalPages || 1,
                total: response?.meta?.total || ((statusFilter !== 'all' || categoryFilter !== 'all' || subcategoryFilter !== 'all' || isPublishedFilter !== 'all' || searchTerm || upcSearch || hsnCode) ? productsArray.length : 0)
            });

        } catch (error) {
            console.error('Fetch products failed:', error);
            setAllProducts([]);
            setFilteredProducts([]);
            setMeta({ page: 1, totalPages: 1, total: 0 });
        } finally {
            setLoading(false);
        }
    };


    const fetchCategories = async () => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('categories/list', null, token);
            setCategories(Array.isArray(response.categories) ? response.categories : []);
        } catch (error) {
            console.error('Fetch categories failed:', error);
        }
    };

    const fetchWarehouses = async () => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const data = await getData('warehouse/list', null, token, false);
            setWarehouseList(data?.warehouse || []);
        } catch (error) {
            setWarehouseList([]);
        }
    };

    useEffect(() => {
        fetchProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, isPublishedFilter, categoryFilter, subcategoryFilter, searchTerm, upcSearch, hsnCode, meta.page]);

    useEffect(() => {
        const fetchFilterSubcategories = async () => {
            try {
                const tokenValue = sessionStorage.getItem('token');
                const token = tokenValue ? `Bearer ${tokenValue}` : '';
                
                let endpoint = 'subcategories/list';
                if (tempFilters.categoryFilter && tempFilters.categoryFilter !== 'all') {
                    endpoint += `?category_id=${tempFilters.categoryFilter}`;
                }
                
                const response = await getData(endpoint, null, token);
                setFilterSubcategories(Array.isArray(response.subcategories) ? response.subcategories : []);
            } catch (error) {
                setFilterSubcategories([]);
                console.error('Fetch filter subcategories failed:', error);
            }
        };
        fetchFilterSubcategories();
    }, [tempFilters.categoryFilter]);

    useEffect(() => {
        let filtered = allProducts;
        
        // Ownership filter
        const vendorRole = localStorage.getItem('vendorRole');
        if (!vendorRole && ownershipFilter !== 'all') {
            filtered = filtered.filter(product => {
                if (ownershipFilter === 'own') return product.ownership === 'Own Product';
                if (ownershipFilter === 'vendor') return product.ownership === 'Vendor Product';
                return true;
            });
        }
        
        setFilteredProducts(filtered);

        // Only update meta if client-side filtering changes the count significantly
        if (!vendorRole && ownershipFilter !== 'all') {
             setMeta(prev => ({
                ...prev,
                total: filtered.length,
                totalPages: Math.ceil(filtered.length / PAGE_SIZE) || 1,
                // Do NOT reset page here to avoid infinite loop with fetchProducts effect
             }));
        }
    }, [allProducts, ownershipFilter]);

    useEffect(() => {
        fetchCategories();
        fetchWarehouses();
    }, []);

    useEffect(() => {
        const fetchSubcategories = async () => {
            if (!form.category_id) {
                setSubcategories([]);
                return;
            }
            try {
                const tokenValue = sessionStorage.getItem('token');
                const token = tokenValue ? `Bearer ${tokenValue}` : '';
                const response = await getData(`subcategories/list?category_id=${form.category_id}`, null, token);
                setSubcategories(Array.isArray(response.subcategories) ? response.subcategories : []);
            } catch (error) {
                setSubcategories([]);
                console.error('Fetch subcategories failed:', error);
            }
        };
        fetchSubcategories();
    }, [form.category_id]);

    const handleAddOrEditProduct = async () => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const userId = sessionStorage.getItem('userId');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const { createdBy, ...formWithoutCreatedBy } = form;
            const requiredFields = [
                'title', 'description', 'key_features', 'how_to_use', 'category_id', 'subcategory_id',
                'unitType', 'unitValue', 'status',  'ideal_for',  'container_type'
            ];
            if (form.unitType === 'weight') requiredFields.push('unitName');
            if (!userId) {
                alert('User ID not found. Please log in again.');
                return;
            }
            const missing = requiredFields.filter(f => !form[f]);
            if (missing.length > 0) {
                alert('Please fill all required fields: ' + missing.join(', '));
                return;
            }
            const formData = new FormData();
            // Define dimension fields to exclude from individual append
            const dimensionFields = ['length', 'width', 'height', 'dimensionUnit', 'weight', 'weightUnit'];
            
            Object.entries(formWithoutCreatedBy).forEach(([key, value]) => {
                if (key === 'product_images' && Array.isArray(value) && value.length > 0) {
                    value.forEach((img, idx) => {
                        if (img instanceof File) {
                            formData.append('product_images', img);
                        } else if (typeof img === 'string' && (img.startsWith('data:image') || img.startsWith('data:video'))) {
                            const arr = img.split(',');
                            const mime = arr[0].match(/:(.*?);/)[1];
                            const bstr = atob(arr[1]);
                            let n = bstr.length;
                            const u8arr = new Uint8Array(n);
                            while (n--) {
                                u8arr[n] = bstr.charCodeAt(n);
                            }
                            const blob = new Blob([u8arr], { type: mime });
                            // Determine extension based on mime type
                            const ext = mime.split('/')[1];
                            formData.append('product_images', blob, `file_${idx}.${ext}`);
                        } else if (typeof img === 'string' && !img.startsWith('data:image') && !img.startsWith('data:video')) {
                            // If it's a URL or path, we might need to send it if the backend expects a list of kept URLs?
                            // Currently skipping re-uploading, assuming backend keeps existing if not sent, 
                            // OR we need to send existing URLs in a separate field?
                            // For now, keeping existing behavior of skipping.
                        }
                    });
                } else if (dimensionFields.includes(key)) {
                    // Skip dimension fields - they'll be sent as a JSON object
                } else if (key === 'unitType') {
                    formData.append('unitType', String(value));
                } else if (key === 'gst') {
                    formData.append('gst', String(value));
                } else if (key !== 'product_images' && key !== 'dimensions') {
                    formData.append(key, value);
                }
            });
            // Append tags as comma-separated string if present
            if (Array.isArray(form.tags) && form.tags.length > 0) {
                formData.set('tags', form.tags.join(','));
            }
            // Append consolidated dimensions object expected by backend
            const dimensions = {
                length: form.length ? Number(form.length) : undefined,
                width: form.width ? Number(form.width) : undefined,
                height: form.height ? Number(form.height) : undefined,
                unit: form.dimensionUnit || undefined,
                weight: form.weight ? Number(form.weight) : undefined,
                weightUnit: form.weightUnit || undefined
            };
            // Remove undefined keys to keep payload clean
            Object.keys(dimensions).forEach(k => dimensions[k] === undefined && delete dimensions[k]);
            if (Object.keys(dimensions).length > 0) {
                formData.append('dimensions', JSON.stringify(dimensions));
            }
            formData.append('createdBy', userId);
            if (editingProduct) {
                await putData(`product/update/${editingProduct._id}`, formData, token);
            } else {
                await postFileUpload('product/add', formData, token);
            }
            setForm(initialFormState);
            setShowAddForm(false);
            setEditingProduct(null);
            fetchProducts();
        } catch (error) {
            console.error('Add/Edit product failed:', error);
        }
    };

    const handleChange = (e) => {
        let updatedForm = { ...form, [e.target.name]: e.target.value };

        // If any B2C price field changes, recalculate selling price
        if (
            ['b2c_mrp', 'b2c_discountType', 'b2c_discountValue'].includes(e.target.name)
        ) {
            const mrp = parseFloat(updatedForm.b2c_mrp) || 0;
            const discountValue = parseFloat(updatedForm.b2c_discountValue) || 0;
            let sellingPrice = mrp;
            if (updatedForm.b2c_discountType === 'flat') {
                sellingPrice = Math.max(0, mrp - discountValue);
            } else if (updatedForm.b2c_discountType === 'percentage') {
                sellingPrice = Math.max(0, mrp - (mrp * Math.min(100, discountValue) / 100));
            }
            updatedForm.b2c_sellingPrice = sellingPrice.toFixed(2);
        }

        // If GST is changed, ensure it's set in form.gst
        if (e.target.name === 'gst') {
            updatedForm.gst = e.target.value;
        }

        if (e.target.name === 'category_id') {
            updatedForm.subcategory_id = '';
        }

        setForm(updatedForm);
    };

    const handleRefresh = () => {
        fetchProducts();
        fetchCategories();
    };

    const handleDeleteClick = (product) => {
        setProductToDelete(product);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (productToDelete) {
            try {
                const tokenValue = sessionStorage.getItem('token');
                const token = tokenValue ? `Bearer ${tokenValue}` : '';
                await deleteData(`product/delete/${productToDelete._id}`, token);
                setDeleteDialogOpen(false);
                setProductToDelete(null);
                fetchProducts();
            } catch (error) {
                console.error('Delete product failed:', error);
            }
        }
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setProductToDelete(null);
    };

    // Pagination handler
    const handlePageChange = (event, newPage) => {
        setMeta((prev) => ({ ...prev, page: newPage }));
    };

    const handleFormModeChange = () => {
        setFormMode(prevMode => prevMode === 'view' ? 'edit' : 'view');
    };

    const handleProductTitleClick = async (product) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData(`stock/list`, null, token, true, '', true);
            const allStock = response?.stock || [];
            
            // Filter stock for this specific product
            const productStock = allStock.filter(stock => 
                stock.product_id?._id === product._id || stock.product_id === product._id
            );
            
            setSelectedProductStock(productStock);
            setSelectedProduct(product);
            setStockDialogOpen(true);
        } catch (error) {
            console.error('Fetch product stock failed:', error);
            setSelectedProductStock([]);
            setSelectedProduct(product);
            setStockDialogOpen(true);
        }
    };

    const handleEditProduct = (product, mode = 'edit') => {
        setEditingProduct(product);
        setFormMode(mode);
        
        // Merge images and videos for display
        const combinedImages = [
            ...(Array.isArray(product.product_images) ? product.product_images : []),
            ...(Array.isArray(product.productVideos) ? product.productVideos : [])
        ].filter(item => item); // Filter out any null/undefined

        const dimensions = typeof product.dimensions === 'string' 
            ? JSON.parse(product.dimensions || '{}') 
            : (product.dimensions || {});

        setForm({
            ...initialFormState,
            ...product,
            ...dimensions, // Spread dimensions fields (length, width, height, etc.)
            dimensionUnit: dimensions.unit || "", // Explicitly map unit to dimensionUnit
            product_images: combinedImages,
            category_id: product.category_id?._id || "",
            subcategory_id: product.subcategory_id?._id || "",
            // ensure numeric values are numbers
            b2c_mrp: product.b2c_mrp || 0,
            b2c_sellingPrice: product.b2c_sellingPrice || 0,
            b2b_sellingPrice: product.b2b_sellingPrice || 0,
            unitValue: product.unitValue || 0,
            gst: product.gst || "",
            isFree: !!product.isFree,
            autoAdd: !!product.autoAdd,
            codAvailable: !!product.codAvailable,
            isPublished: !!product.isPublished,
            tags: Array.isArray(product.tags) ? product.tags : [],
            
        });
        setShowAddForm(true);
    };

    return (
        <Box sx={{ p: 1 }}>
            <Paper elevation={2} sx={{ p: 1, boxShadow: 'none' }}>
                {!showAddForm && (
                    <>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            flexWrap: 'wrap',
                            gap: 2
                        }}>
                            <Typography component="h1" variant="h5"
                                sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700, color: "#1a1a1a", display: 'flex', alignItems: 'center', gap: 1 }}>
                                Product Management
                                <Chip
                                    label={`${meta.total} ${meta.total === 1 ? 'Item' : 'Items'}`}
                                    size="small"
                                    sx={{ 
                                        color: '#d32f2f', 
                                        bgcolor: 'rgba(211, 47, 47, 0.1)',
                                        fontFamily: 'Mulish, sans-serif',
                                        fontWeight: 700,
                                        borderRadius: '6px',
                                        height: 24
                                    }}
                                />
                            </Typography>

                            <Stack direction="row" spacing={2}>
                                 <Button
                                variant="outlined"
                                startIcon={<FilterIcon />}
                                sx={{
                                    fontFamily: 'Mulish, sans-serif',
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: '#dee2e6',
                                    color: '#495057',
                                    '&:hover': { 
                                        borderColor: '#adb5bd',
                                        backgroundColor: '#f8f9fa' 
                                    }
                                }}
                                onClick={() => {
                                    setTempFilters({
                                        searchTerm,
                                        statusFilter,
                                        categoryFilter,
                                        ownershipFilter: isVendor ? 'vendor' : ownershipFilter,
                                    });
                                    setFilterDrawerOpen(true);
                                }}
                            >
                                Filters
                            </Button>
                               
                                <Button
                                    variant="contained"
                                    startIcon={<FaRegCheckCircle />}
                                    sx={{
                                        fontFamily:'Mulish, sans-serif',
                                        background: '#2B333A',
                                        color: 'white',
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        px: 3,
                                        boxShadow: '0 4px 12px rgba(43, 51, 58, 0.2)',
                                        '&:hover': {
                                            background: '#1a1f24',
                                            boxShadow: '0 6px 16px rgba(43, 51, 58, 0.3)',
                                        }
                                    }}
                                    onClick={() => {
                                        setUpcModalOpen(true);
                                        setUpcInput('');
                                        setUpcVerifyResult(null);
                                    }}
                                >
                                    Check UPC
                                </Button>
                              
            {/* UPC Modal */}
            <Dialog open={upcModalOpen} onClose={() => setUpcModalOpen(false)} >
                <DialogTitle sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700 }}>Check UPC Code</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Enter UPC Code"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={upcInput}
                        onChange={e => setUpcInput(e.target.value)}
                        sx={{ mb: 2, fontFamily: 'Mulish, sans-serif',width:'400px' }}
                    />
                    {/* No inline result, handled in new modal */}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUpcModalOpen(false)}  sx={{ fontFamily: 'Mulish, sans-serif', color:'red' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            setUpcVerifying(true);
                            setUpcVerifyResult(null);
                            try {
                                const tokenValue = sessionStorage.getItem('token');
                                const token = tokenValue ? `Bearer ${tokenValue}` : '';
                                const payload = { upc: upcInput.trim() };
                                const response = await postData("product/check-upc", payload, token);
                                setUpcVerifyResult(response);
                                setUpcResultModalOpen(true);
                            } catch (err) {
                                setUpcVerifyResult({ message: 'Error verifying UPC.', type: 'error' });
                                setUpcResultModalOpen(true);
                            } finally {
                                setUpcVerifying(false);
                            }
                        }}
                        disabled={!upcInput || upcVerifying}
                        sx={{ fontFamily: 'Mulish, sans-serif', background: '#2B333A',color:'white', '&:hover': { background: '#2B333A' } }}
                    >
                        {upcVerifying ? <CircularProgress size={20} /> : 'Verify'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* UPC Result Modal */}
            <Dialog
                open={upcResultModalOpen}
                onClose={() => setUpcResultModalOpen(false)}
                PaperProps={{
                    style: {
                        borderRadius: '10px',
                        padding: '5px',
                        minWidth: '500px',
                        // maxWidth: '90vw',
                    }
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontSize: '20px', fontWeight: 'bold', textAlign: 'center', fontFamily: 'Mulish, sans-serif' }}>
                    <img
                        style={{ width: '180px' }}
                        src={
                            upcVerifyResult?.type === 'new' ? '/img/new.png' :
                            upcVerifyResult?.type === 'not-available' ? '/img/not.png' :
                            upcVerifyResult?.type === 'existing' ? '/img/send.png' :
                            '/img/nodata.png'
                        }
                        alt="upc result"
                    />
                </DialogTitle>
                <DialogContent>
                    {upcVerifyResult && (
                        <>
                            <DialogContentText sx={{ textAlign: 'center', fontSize: '16px', mb: 1, fontFamily: 'Mulish, sans-serif' }}>
                                <b>{upcVerifyResult.message}</b>
                            </DialogContentText>
                            {upcVerifyResult.type === 'new' && (
                                <Box sx={{
                                    textAlign: 'center',
                                    backgroundColor: '#d4edda',
                                    padding: '12px',
                                    borderRadius: '4px',
                                    borderLeft: '4px solid #28a745',
                                    marginBottom: '2px',
                                    color: '#155724',
                                    fontFamily: 'Mulish, sans-serif'
                                }}>
                                    This UPC is available for a new product. You can proceed to add it.
                                </Box>
                            )}
                            {upcVerifyResult.type === 'not-available' && (
                                <Box sx={{
                                    textAlign: 'center',
                                    backgroundColor: '#f8d7da',
                                    padding: '12px',
                                    borderRadius: '4px',
                                    borderLeft: '4px solid #dc3545',
                                    marginBottom: '2px',
                                    color: '#721c24',
                                    fontFamily: 'Mulish, sans-serif'
                                }}>
                                    This UPC is already supplied by another vendor. Please try a different UPC.
                                </Box>
                            )}
                            {upcVerifyResult.type === 'existing' && (
                                <Box sx={{
                                    textAlign: 'center',
                                    backgroundColor: '#fff3cd',
                                    padding: '12px',
                                    borderRadius: '4px',
                                    borderLeft: '4px solid #ffc107',
                                    marginBottom: '2px',
                                    color: '#856404',
                                    fontFamily: 'Mulish, sans-serif'
                                }}>
                                    Product managed by admin. You can request to supply as Vendor if stock is not available.
                                </Box>
                            )}
                            {(!['new','not-available','existing'].includes(upcVerifyResult.type)) && (
                                <Box sx={{
                                    textAlign: 'center',
                                    backgroundColor: '#e2e3e5',
                                    padding: '12px',
                                    borderRadius: '4px',
                                    borderLeft: '4px solid #bfc1c2',
                                    marginBottom: '2px',
                                    color: '#383d41',
                                    fontFamily: 'Mulish, sans-serif'
                                }}>
                                    {upcVerifyResult.message || 'Unknown response.'}
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: '20px', padding: '20px' }}>
                    {upcVerifyResult?.type === 'not-available' && (
                    <Button
                        onClick={() => setUpcResultModalOpen(false)}
                        variant="contained"
                        sx={{
                                        fontFamily:'Mulish, sans-serif',
                                        background: '#2B333A',
                                        color: 'black',
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        border:'1px solid black',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            background: '#2B333A'
                                        }
                                    }}
                        autoFocus
                    >
                        Close
                    </Button>
                    )}
                    {upcVerifyResult?.type === 'new' && (
                    <Button
                                    variant="contained"
                                    startIcon={<MdAddShoppingCart />}
                                    onClick={() => setShowAddForm(true)}
                                    sx={{
                                        fontFamily:'Mulish, sans-serif',
                                        background: '#2B333A',
                                        color: 'white',
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        // border:'1px solid black',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            background: '#2B333A'
                                        }
                                    }}
                                >
                                   Add  Product
                                </Button>
                    )}
                    {upcVerifyResult?.type === 'existing'  && (
                    <Button
                        variant="contained"
                        startIcon={<MdPresentToAll />}
                        sx={{
                            fontFamily:'Mulish, sans-serif',
                            background: '#2B333A',
                            color: 'white',
                            textTransform: 'none',
                            borderRadius: 2,
                            // border:'1px solid black',
                            boxShadow: 'none',
                            '&:hover': {
                                background: '#2B333A'
                            }
                        }}
                        onClick={() => {
                            setRequestForm({
                                upc: upcInput,
                                maximumRetailPrice: '',
                                b2c_sellingPrice: '',
                                b2b_sellingPrice: ''
                            });
                            setRequestFormModalOpen(true);
                        }}
                    >
                        Request form
                    </Button>
                    )}
            {/* Request Form Modal */}
            <Dialog
                open={requestFormModalOpen}
                onClose={() => setRequestFormModalOpen(false)}
                PaperProps={{
                    style: {
                        borderRadius: '10px',
                        padding: '10px',
                        minWidth: '500px',
                    }
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontSize: '20px', fontWeight: 'bold', textAlign: 'center', fontFamily: 'Mulish, sans-serif' }}>
                    Request to Supply Product
                </DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="UPC"
                            value={requestForm.upc}
                            InputProps={{ readOnly: true }}
                            fullWidth
                            sx={{ fontFamily: 'Mulish, sans-serif' }}
                        />
                        <TextField
                            label="Maximum Retail Price (MRP)"
                            type="number"
                            value={requestForm.maximumRetailPrice}
                            onChange={e => setRequestForm(f => ({ ...f, maximumRetailPrice: e.target.value }))}
                            fullWidth
                            sx={{ fontFamily: 'Mulish, sans-serif' }}
                        />
                        <TextField
                            label="B2C Selling Price"
                            type="number"
                            value={requestForm.b2c_sellingPrice}
                            onChange={e => setRequestForm(f => ({ ...f, b2c_sellingPrice: e.target.value }))}
                            fullWidth
                            sx={{ fontFamily: 'Mulish, sans-serif' }}
                        />
                        <TextField
                            label="B2B Selling Price"
                            type="number"
                            value={requestForm.b2b_sellingPrice}
                            onChange={e => setRequestForm(f => ({ ...f, b2b_sellingPrice: e.target.value }))}
                            fullWidth
                            sx={{ fontFamily: 'Mulish, sans-serif' }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: '20px', padding: '20px' }}>
                    <Button
                        onClick={() => setRequestFormModalOpen(false)}
                        variant="outlined"
                        sx={{
                            minWidth: '120px',
                            borderColor: 'gray',
                            color: 'gray',
                            '&:hover': {
                                borderColor: 'darkgray',
                                backgroundColor: 'rgba(0,0,0,0.05)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {
                            setRequestFormSubmitting(true);
                            try {
                                const tokenValue = sessionStorage.getItem('token');
                                const token = tokenValue ? `Bearer ${tokenValue}` : '';
                                // Replace with your actual API endpoint
                                const payload = {
                                    upc: requestForm.upc,
                                    maximumRetailPrice: requestForm.maximumRetailPrice,
                                    b2c_sellingPrice: requestForm.b2c_sellingPrice,
                                    b2b_sellingPrice: requestForm.b2b_sellingPrice
                                };
                                await postData('product/vendor-assign-request', payload, token);
                                setRequestFormModalOpen(false);
                                // Optionally show a success message
                            } catch (err) {
                                // Optionally show an error message
                            } finally {
                                setRequestFormSubmitting(false);
                            }
                        }}
                        variant="contained"
                        disabled={
                            !requestForm.maximumRetailPrice ||
                            !requestForm.b2c_sellingPrice ||
                            !requestForm.b2b_sellingPrice ||
                            requestFormSubmitting
                        }
                        sx={{
                            minWidth: '120px',
                            backgroundColor: '#2B333A',
                            color: 'white',
                            border: '1px solid black',
                            fontFamily: 'Mulish, sans-serif',
                            '&:hover': {
                                backgroundColor: '#2B333A'
                            }
                        }}
                    >
                        {requestFormSubmitting ? <CircularProgress size={20} /> : 'Send Request'}
                    </Button>
                </DialogActions>
            </Dialog>
                </DialogActions>
            </Dialog>
                            </Stack>
                        </Box>

                    </>
                )}

                {!showAddForm ? (
                    <>
                        {loading && filteredProducts.length === 0 && false ? (
                            <Box sx={{ p: 2 }}>
                                <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height={100}
                                    animation="pulse"
                                    sx={{ mb: 2 }}
                                />
                                <Skeleton variant="text" width='100%' animation="pulse" />
                                <Skeleton variant="text" width='100%' animation="pulse" />
                                <Skeleton variant="text" width='100%' animation="pulse" />
                            </Box>
                        ) : (
                            <>
                                <TableContainer
                                    component={Paper}
                                    elevation={0}
                                    sx={{
                                        borderRadius: '12px',
                                        border: '1px solid #e0e0e0',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <Table sx={{ minWidth: 650 }} aria-label="product table">
                                        <TableHead>
                                            <TableRow sx={{
                                                backgroundColor: '#f8f9fa',
                                                '& th': {
                                                    fontFamily: 'Mulish, sans-serif',
                                                    fontWeight: 700,
                                                    fontSize: '0.875rem',
                                                    color: '#495057',
                                                    borderBottom: '2px solid #e9ecef',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    py: 2
                                                }
                                            }}>
                                                <TableCell align="center">Image</TableCell>
                                                <TableCell sx={{ pl: 4 }}>Product Details</TableCell>
                                                <TableCell align="center">Available</TableCell>
                                                <TableCell align="center">Min. Stock</TableCell>
                                                <TableCell align="center">Category</TableCell>
                                                <TableCell align="center">Status</TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loading ? (
                                                Array.from({ length: 6 }).map((_, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell align="center">
                                                            <Skeleton variant="rounded" width={50} height={50} sx={{ mx: 'auto', borderRadius: '8px' }} />
                                                        </TableCell>
                                                        <TableCell sx={{ pl: 4 }}>
                                                            <Skeleton width="60%" />
                                                            <Skeleton width="40%" />
                                                        </TableCell>
                                                        <TableCell align="center"><Skeleton width={40} sx={{ mx: 'auto' }} /></TableCell>
                                                        <TableCell align="center"><Skeleton width={40} sx={{ mx: 'auto' }} /></TableCell>
                                                        <TableCell align="center"><Skeleton width={80} sx={{ mx: 'auto' }} /></TableCell>
                                                        <TableCell align="center"><Skeleton width={70} sx={{ mx: 'auto' }} /></TableCell>
                                                        <TableCell align="center"><Skeleton width={60} sx={{ mx: 'auto' }} /></TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                filteredProducts.length > 0 ? (
                                                filteredProducts.map((product) => {
                                                    const imageUrl = product.product_images?.[0]
                                                        ? product.product_images[0].startsWith('http')
                                                            ? product.product_images[0]
                                                            : `https://api.tvfm.digitaltriumph.co.in/${product.product_images[0]}`
                                                        : '/img/default-product.png';
                                                    const status = statusOptions.find(opt => opt.value === product.status) ||
                                                        { label: product.status || 'Unknown', color: 'default' };
                                                    return (
                                                        <TableRow
                                                            key={product._id}
                                                            hover
                                                            sx={{ 
                                                                '&:last-child td': { borderBottom: 0 },
                                                                '&:hover': { backgroundColor: '#f8f9fa' },
                                                                '& td': {
                                                                    py: 1.5,
                                                                    borderBottom: '1px solid #f1f3f5',
                                                                    fontFamily: 'Mulish, sans-serif'
                                                                }
                                                            }}
                                                        >
                                                            <TableCell align="center">
                                                                <Box sx={{ 
                                                                    width: 50, 
                                                                    height: 50, 
                                                                    borderRadius: '8px', 
                                                                    overflow: 'hidden', 
                                                                    mx: 'auto',
                                                                    border: '1px solid #dee2e6',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    backgroundColor: '#fff'
                                                                }}>
                                                                    <img
                                                                        src={imageUrl}
                                                                        alt={product.title}
                                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                    />
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell sx={{ pl: 4 }}>
                                                                <Typography fontWeight={600} variant="body2" sx={{ color: '#212529', fontSize: '0.95rem', fontFamily:'Mulish, sans-serif' }}>
                                                                    {product.title}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily:'Mulish, sans-serif' }}>
                                                                    UPC: {product.upc || 'N/A'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Typography fontWeight={600} sx={{ fontFamily:'Mulish, sans-serif', color: product.availableQuantity < 10 ? '#dc3545' : '#212529' }}>
                                                                    {product.availableQuantity}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Typography variant="body2" sx={{ fontFamily:'Mulish, sans-serif' }}>
                                                                    {product.minimumStockQuantity}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Stack alignItems="center" spacing={0.5}>
                                                                    <Chip
                                                                        label={product.category_id?.title || '-'}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ 
                                                                            borderColor: '#e0e0e0', 
                                                                            color: '#666', 
                                                                            fontFamily: 'Mulish, sans-serif',
                                                                            height: 24,
                                                                            fontSize: '0.75rem'
                                                                        }}
                                                                    />
                                                                    {product.subcategory_id?.title && (
                                                                         <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.7rem' }}>
                                                                            {product.subcategory_id.title}
                                                                        </Typography>
                                                                    )}
                                                                </Stack>
                                                            </TableCell>
                                                            {/* <TableCell>
                                                                <Typography fontWeight={500} sx={{fontFamily:'Mulish, sans-serif',}}>
                                                                    {product.unitValue} {product.unitName || ''}
                                                                </Typography>
                                                            </TableCell> */}
                                                            <TableCell align="center">
                                                                <Chip
                                                                    label={status.label}
                                                                    size="small"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        textTransform: 'capitalize',
                                                                        backgroundColor: status.value === 'active' || status.value === 'approved' ? 'rgba(40, 167, 69, 0.1)' : 
                                                                                        status.value === 'pending' ? 'rgba(255, 193, 7, 0.1)' :
                                                                                        'rgba(108, 117, 125, 0.1)',
                                                                        color: status.value === 'active' || status.value === 'approved' ? '#28a745' : 
                                                                               status.value === 'pending' ? '#ffc107' : 
                                                                               '#6c757d',
                                                                        fontFamily: 'Mulish, sans-serif',
                                                                        px: 1,
                                                                        height: 28
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                                    <Tooltip title="Edit">
                                                                        <IconButton
                                                                            size="small"
                                                                            sx={{ 
                                                                                color: '#0d6efd', 
                                                                                backgroundColor: 'rgba(13, 110, 253, 0.05)',
                                                                                '&:hover': { backgroundColor: 'rgba(13, 110, 253, 0.15)' }
                                                                            }}
                                                                            onClick={() => handleEditProduct(product, 'edit')}
                                                                        >
                                                                            <FaEdit size={16} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Delete">
                                                                        <IconButton
                                                                            size="small"
                                                                            sx={{ 
                                                                                color: '#dc3545', 
                                                                                backgroundColor: 'rgba(220, 53, 69, 0.05)',
                                                                                transition: 'all 0.2s',
                                                                                '&:hover': { 
                                                                                    backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                                                                    transform: 'scale(1.1)'
                                                                                }
                                                                            }}
                                                                            onClick={() => handleDeleteClick(product)}
                                                                        >
                                                                            <MdDeleteForever size={18} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={7} sx={{
                                                        textAlign: 'center',
                                                        py: 8,
                                                    }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            opacity: 0.7
                                                        }}>
                                                            <img
                                                                style={{ width: '120px', marginBottom: 16, opacity: 0.8 }}
                                                                src='./img/nodata.png' alt='nodata' />
                                                            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, color: '#495057' }}>
                                                                No products found
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' || ownershipFilter !== 'all'
                                                                    ? 'Try adjusting your filters'
                                                                    : 'No products available'}
                                                            </Typography>
                                                            <Button
                                                                variant="outlined"
                                                                onClick={() => {
                                                            setTempFilters({
                                                                searchTerm: '',
                                                                upcSearch: '',
                                                                hsnCode: '',
                                                                statusFilter: 'all',
                                                                isPublishedFilter: 'all',
                                                                categoryFilter: 'all',
                                                                subcategoryFilter: 'all',
                                                                ownershipFilter: 'all',
                                                            });
                                                            setSearchTerm('');
                                                            setUpcSearch('');
                                                            setHsnCode('');
                                                            setStatusFilter('all');
                                                            setIsPublishedFilter('all');
                                                            setCategoryFilter('all');
                                                            setSubcategoryFilter('all');
                                                            setOwnershipFilter('all');
                                                            setMeta(prev => ({ ...prev, page: 1 }));
                                                            setFilterDrawerOpen(false);
                                                        }}
                                                                sx={{ mt: 2, textTransform: 'none' }}
                                                            >
                                                                Clear filters
                                                            </Button>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {/* Pagination Component (centered, like Categories/ActivityLog) */}
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, '& .MuiPagination-ul': { flexWrap: 'nowrap' } }}>
                                    <Pagination
                                        count={meta.totalPages}
                                        page={meta.page}
                                        onChange={handlePageChange}
                                        color="primary"
                                        shape="rounded"
                                        showFirstButton
                                        showLastButton
                                    />
                                </Box>
                                {meta.total > 0 && (
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                        Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} entries
                                    </Typography>
                                )}
                                {loading && filteredProducts.length > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <ProductForm
                        form={form}
                        onFormChange={handleChange}
                        onCancel={() => {
                            setShowAddForm(false);
                            setEditingProduct(null);
                            setForm(initialFormState);
                        }}
                        onSubmit={handleAddOrEditProduct}
                        isSubmitDisabled={!form.title}
                        categories={categories}
                        subcategories={subcategories}
                        isEdit={true}
                    formMode={'edit'}
                    handleFormModeChange={handleFormModeChange}
                    />
                )}

                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleCloseDeleteDialog}
                    PaperProps={{
                        style: {
                            borderRadius: '10px',
                            padding: '20px',
                            minWidth: '400px'
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: '20px', fontWeight: 'bold', textAlign: 'center' }}>
                        <img style={{ width: '80px' }} src='./img/del1.png' alt='delete' />
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ textAlign: 'center', fontSize: '16px', mb: 1 }}>
                            Are you sure you want to delete product <b>"{productToDelete?.title}"</b> and all its variants?
                        </DialogContentText>
                        <Box sx={{
                            textAlign: 'center',
                            backgroundColor: '#fff3cd',
                            padding: '12px',
                            borderRadius: '4px',
                            borderLeft: '4px solid #ffc107',
                            marginBottom: '2px'
                        }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#856404', marginBottom: '3px' }}>
                                Warning
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#856404' }}>
                                By deleting, you won't be able to access it again.
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', gap: '20px', padding: '20px' }}>
                        <Button
                            onClick={handleCloseDeleteDialog}
                            variant="outlined"
                            sx={{
                                minWidth: '120px',
                                borderColor: 'gray',
                                color: 'gray',
                                '&:hover': {
                                    borderColor: 'darkgray',
                                    backgroundColor: 'rgba(0,0,0,0.05)'
                                }
                            }}
                        >
                            No, Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            variant="contained"
                            sx={{
                                minWidth: '120px',
                                backgroundColor: 'red',
                                '&:hover': {
                                    backgroundColor: '#bb2d3b'
                                }
                            }}
                            autoFocus
                        >
                            Yes, Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* <Dialog 
                    open={stockDialogOpen} 
                    onClose={() => setStockDialogOpen(false)} 
                    maxWidth="lg" 
                    fullWidth
                >
                    <DialogTitle>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" sx={{fontFamily:'Mulish, sans-serif',}}>
                                Stock Details for: {selectedProduct?.title}
                            </Typography>
                            <Button 
                                onClick={() => setStockDialogOpen(false)}
                                sx={{ color: 'red' }}
                            >
                                Close
                            </Button>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        {selectedProductStock.length > 0 ? (
                            <TableContainer component={Paper} sx={{ mt: 2 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#2B333A' }}>
                                            <TableCell sx={{ fontWeight: 'bold', fontFamily:'Mulish, sans-serif', fontSize: 16, color: 'text.secondary' }}>Batch No</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontFamily:'Mulish, sans-serif', fontSize: 16, color: 'text.secondary' }}>Warehouse</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontFamily:'Mulish, sans-serif', fontSize: 16, color: 'text.secondary' }}>Quantity</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontFamily:'Mulish, sans-serif', fontSize: 16, color: 'text.secondary' }}>MRP</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontFamily:'Mulish, sans-serif', fontSize: 16, color: 'text.secondary' }}>Selling Price</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontFamily:'Mulish, sans-serif', fontSize: 16, color: 'text.secondary' }}>Purchase Price</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontFamily:'Mulish, sans-serif', fontSize: 16, color: 'text.secondary' }}>Manufacture Date</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontFamily:'Mulish, sans-serif', fontSize: 16, color: 'text.secondary' }}>Expire Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedProductStock.map((stock, index) => {
                                            const warehouseObj = warehouseList.find(w => w._id === stock.warehouseId);
                                            const warehouseName = warehouseObj ? warehouseObj.name : stock.warehouseId;
                                            
                                            return (
                                                <TableRow key={stock._id || index} hover>
                                                    <TableCell sx={{fontFamily:'Mulish, sans-serif'}}>{stock.batch_no}</TableCell>
                                                    <TableCell sx={{fontFamily:'Mulish, sans-serif'}}>{warehouseName}</TableCell>
                                                    <TableCell sx={{fontFamily:'Mulish, sans-serif'}}>{stock.quantity}</TableCell>
                                                    <TableCell sx={{fontFamily:'Mulish, sans-serif'}}>{stock.maximumRetailPrice}</TableCell>
                                                    <TableCell sx={{fontFamily:'Mulish, sans-serif'}}>{stock.sellingPrice}</TableCell>
                                                    <TableCell sx={{fontFamily:'Mulish, sans-serif'}}>{stock.purchasePrice}</TableCell>
                                                    <TableCell sx={{fontFamily:'Mulish, sans-serif'}}>
                                                        {stock.manufactureDate ? stock.manufactureDate.slice(0, 10) : ''}
                                                    </TableCell>
                                                    <TableCell sx={{fontFamily:'Mulish, sans-serif'}}>
                                                        {stock.expireDate ? stock.expireDate.slice(0, 10) : ''}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box sx={{ 
                                textAlign: 'center', 
                                py: 4,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 2
                            }}>
                                <img
                                    style={{ width: '150px' }}
                                    src='./img/nodata.png' 
                                    alt='no stock data' 
                                />
                                <Typography variant="h6" sx={{fontFamily:'Mulish, sans-serif'}}>
                                    No stock available for this product
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{fontFamily:'Mulish, sans-serif'}}>
                                    This product doesn't have any stock entries yet.
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                </Dialog> */}
            </Paper>

            <Drawer
                anchor="right"
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
            >
                <Box sx={{ width: 350, p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Mulish, sans-serif' }}>
                        Filters
                    </Typography>

                    <TextField
                        label="Search by Title"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={tempFilters.searchTerm}
                        onChange={(e) => setTempFilters({ ...tempFilters, searchTerm: e.target.value })}
                        sx={{ mb: 2, borderRadius: 2 }}
                    />
                    
                    <TextField
                        label="Search by UPC"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={tempFilters.upcSearch}
                        onChange={(e) => setTempFilters({ ...tempFilters, upcSearch: e.target.value })}
                        sx={{ mb: 2, borderRadius: 2 }}
                    />

                    <TextField
                        label="Search by HSN Code"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={tempFilters.hsnCode}
                        onChange={(e) => setTempFilters({ ...tempFilters, hsnCode: e.target.value })}
                        sx={{ mb: 2, borderRadius: 2 }}
                    />
                  
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={tempFilters.statusFilter}
                            onChange={e => setTempFilters({ ...tempFilters, statusFilter: e.target.value })}
                            label="Status"
                            sx={{ borderRadius: 2 }}
                        >
                            <MenuItem value="all">All Statuses</MenuItem>
                            {statusOptions.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Published Status</InputLabel>
                        <Select
                            value={tempFilters.isPublishedFilter}
                            onChange={e => setTempFilters({ ...tempFilters, isPublishedFilter: e.target.value })}
                            label="Published Status"
                            sx={{ borderRadius: 2 }}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="published">Published</MenuItem>
                            <MenuItem value="unpublished">Unpublished</MenuItem>
                        </Select>
                    </FormControl>
                    <Autocomplete
                        options={Array.isArray(categories) ? categories : []}
                        getOptionLabel={option => option.title || ""}
                        value={
                            categories.find(cat => cat._id === tempFilters.categoryFilter) ||
                            (tempFilters.categoryFilter === "all" ? { _id: "all", title: "All Categories" } : null)
                        }
                        onChange={(event, newValue) => {
                            setTempFilters({ ...tempFilters, categoryFilter: newValue ? newValue._id : "all" });
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Category"
                                size="small"
                                fullWidth
                                placeholder="Search categories..."
                                sx={{ borderRadius: 2 }}
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        ListboxProps={{
                            style: { maxHeight: 144, overflow: 'auto' }
                        }}
                        PaperComponent={({ children }) => (
                            <Paper elevation={8} sx={{ marginTop: 1, borderRadius: 2 }}>
                                {children}
                            </Paper>
                        )}
                        noOptionsText="No categories found"
                        sx={{ mb: 2 }}
                    />
                    
                     <Autocomplete
                        options={filterSubcategories}
                        getOptionLabel={option => option.title || ""}
                        value={
                            filterSubcategories.find(sub => sub._id === tempFilters.subcategoryFilter) ||
                            (tempFilters.subcategoryFilter === "all" ? null : null)
                        }
                        onChange={(event, newValue) => {
                            setTempFilters({ ...tempFilters, subcategoryFilter: newValue ? newValue._id : "all" });
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Sub Category"
                                size="small"
                                fullWidth
                                placeholder="Search subcategories..."
                                sx={{ borderRadius: 2 }}
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        ListboxProps={{
                            style: { maxHeight: 144, overflow: 'auto' }
                        }}
                         PaperComponent={({ children }) => (
                            <Paper elevation={8} sx={{ marginTop: 1, borderRadius: 2 }}>
                                {children}
                            </Paper>
                        )}
                        noOptionsText="No subcategories found"
                        sx={{ mb: 2 }}
                    />
                   
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                            setTempFilters({
                                searchTerm: '',
                                upcSearch: '',
                                hsnCode: '',
                                statusFilter: 'all',
                                isPublishedFilter: 'all',
                                categoryFilter: 'all',
                                subcategoryFilter: 'all',
                                ownershipFilter: 'all',
                            });
                            setSearchTerm('');
                            setUpcSearch('');
                            setHsnCode('');
                            setStatusFilter('all');
                            setIsPublishedFilter('all');
                            setCategoryFilter('all');
                            setSubcategoryFilter('all');
                            setOwnershipFilter('all');
                            setMeta(prev => ({ ...prev, page: 1 }));
                            setFilterDrawerOpen(false);
                        }}
                        sx={{ fontFamily: 'Mulish, sans-serif', borderRadius: 2, mt: 1, color: 'black', border: '1px solid black', }}
                    >
                        Clear
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        // color="primary"
                        onClick={() => {
                            let changed = false;
                            if (searchTerm !== tempFilters.searchTerm) {
                                setSearchTerm(tempFilters.searchTerm);
                                changed = true;
                            }
                            if (upcSearch !== tempFilters.upcSearch) {
                                setUpcSearch(tempFilters.upcSearch);
                                changed = true;
                            }
                            if (hsnCode !== tempFilters.hsnCode) {
                                setHsnCode(tempFilters.hsnCode);
                                changed = true;
                            }
                            if (statusFilter !== tempFilters.statusFilter) {
                                setStatusFilter(tempFilters.statusFilter);
                                changed = true;
                            }
                            if (isPublishedFilter !== tempFilters.isPublishedFilter) {
                                setIsPublishedFilter(tempFilters.isPublishedFilter);
                                changed = true;
                            }
                            if (categoryFilter !== tempFilters.categoryFilter) {
                                setCategoryFilter(tempFilters.categoryFilter);
                                changed = true;
                                
                            }
                            if (ownershipFilter !== tempFilters.ownershipFilter) {
                                setOwnershipFilter(tempFilters.ownershipFilter);
                                changed = true;
                            }
                            if (subcategoryFilter !== tempFilters.subcategoryFilter) {
                                setSubcategoryFilter(tempFilters.subcategoryFilter);
                                changed = true;
                            }
                            // Always reset to first page, which will trigger useEffect
                            setMeta(prev => ({ ...prev, page: 1 }));
                            setFilterDrawerOpen(false);
                        }}
                        sx={{ fontFamily: 'Mulish, sans-serif', borderRadius: 2, mt: 2 , background:"#2B333A",color:"white", border:"1px solid black",
                            '&:hover': { backgroundColor: '#2B333A'}
                        }}
                    >
                        Search
                    </Button>
                </Box>
            </Drawer>
        </Box>
    );
};

export default ProductManagement;